import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { AppMessage, MessageAudience, MessageButton, MessageRenderContext } from '@/types/message';
import * as Sentry from '@sentry/react';
import { isFirebaseOfflineError } from '@/services/errorReportingService';
import { passesVersionGate } from '@/utils/versionGate';

const messagesCollection = collection(db, 'messages');

// Helper function to convert timestamp objects to Firestore Timestamps
const convertToTimestamp = (timestampObj: unknown): Timestamp => {
  if (timestampObj instanceof Timestamp) {
    return timestampObj;
  }
  if (timestampObj && typeof timestampObj === 'object' && 'seconds' in timestampObj) {
    const { seconds, nanoseconds } = timestampObj as { seconds: number; nanoseconds?: number };
    return new Timestamp(seconds, nanoseconds || 0);
  }
  throw new Error('Invalid timestamp format');
};

const matchesAudience = (audience: MessageAudience | undefined, ctx: MessageRenderContext): boolean => {
  if (!audience) {
    return true;
  }
  if (Array.isArray(audience.planNames) && audience.planNames.length > 0) {
    if (!ctx.planName || !audience.planNames.includes(ctx.planName)) {
      return false;
    }
  }
  if (typeof audience.maxAiProcessedCount === 'number') {
    if (ctx.aiProcessedCount > audience.maxAiProcessedCount) {
      return false;
    }
  }
  return true;
};

const isSafeButtonUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('?')) {
    return true;
  }
  try {
    const parsed = new URL(trimmed, window.location.origin);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' || parsed.protocol === 'mailto:';
  } catch {
    return false;
  }
};

const interpolate = (template: string | undefined, ctx: MessageRenderContext): string | undefined => {
  if (!template) {
    return template;
  }
  const hasCap = typeof ctx.maxAiNotes === 'number';
  const remaining = hasCap ? Math.max(0, (ctx.maxAiNotes as number) - ctx.aiProcessedCount) : null;
  return template
    .replaceAll('{aiUsed}', String(ctx.aiProcessedCount))
    .replaceAll('{aiCap}', hasCap ? String(ctx.maxAiNotes) : '')
    .replaceAll('{aiRemaining}', remaining === null ? '' : String(remaining))
    .replaceAll('{planName}', ctx.planName ?? '');
};

/** Absent or non-numeric priority sorts after every explicit one. */
const sortPriority = (priority: unknown): number =>
  typeof priority === 'number' && Number.isFinite(priority) ? priority : Number.MAX_SAFE_INTEGER;

export const getActiveMessage = async (ctx: MessageRenderContext): Promise<AppMessage | null> => {
  const now = Timestamp.now();

  try {
    // Get all active messages and filter manually due to timestamp conversion issues
    const activeQuery = query(messagesCollection, where('active', '==', true));
    const querySnapshot = await getDocs(activeQuery);

    if (querySnapshot.empty) {
      return null;
    }

    // Filter and convert messages manually
    const validMessages: AppMessage[] = [];

    querySnapshot.forEach((doc) => {
      try {
        const data = doc.data();
        const startDate = convertToTimestamp(data.startDate);
        const endDate = convertToTimestamp(data.endDate);

        // Check if message is currently active
        if (startDate.toMillis() <= now.toMillis() && endDate.toMillis() >= now.toMillis()) {
          const audience = (data.audience as MessageAudience | undefined) || undefined;
          if (!matchesAudience(audience, ctx)) {
            return;
          }
          // Optional release-version gate: a banner tagged with the version it
          // announces is hidden once this user's desktop app is already on it.
          // Unknown desktop version fails OPEN here — most web visitors have no
          // desktop app and must still see release news. See passesVersionGate.
          const releaseVersion = (data.releaseVersion as string | null | undefined) ?? null;
          if (!passesVersionGate(releaseVersion, ctx.desktopVersion)) {
            return;
          }
          const rawButton = data.button as MessageButton | undefined;
          const button =
            rawButton && rawButton.text?.trim() && rawButton.url?.trim() && isSafeButtonUrl(rawButton.url)
              ? {
                  text: interpolate(rawButton.text, ctx) ?? rawButton.text,
                  url: rawButton.url.trim(),
                  style: rawButton.style,
                }
              : undefined;
          validMessages.push({
            id: doc.id,
            message: interpolate(data.message, ctx) ?? '',
            priority: data.priority,
            startDate: startDate,
            endDate: endDate,
            active: data.active,

            severity: data.severity || 'info',
            content: interpolate(data.content, ctx) || undefined,
            audience,
            button,
            releaseVersion,
          } as AppMessage);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'message-service' },
          extra: { context: 'processing message', messageId: doc.id },
        });
      }
    });

    if (validMessages.length === 0) {
      return null;
    }

    // Lowest priority number wins. Most live docs carry NO `priority`, and a bare
    // `a.priority - b.priority` is NaN for them, which left the winner up to
    // Firestore's stream order. Absent sorts LAST, ties fall back to doc id — the
    // same order the desktop app's appMessages.ts uses, so both clients agree.
    validMessages.sort((a, b) => sortPriority(a.priority) - sortPriority(b.priority) || a.id.localeCompare(b.id));
    return validMessages[0];
  } catch (error) {
    if (!isFirebaseOfflineError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'message-service' },
        extra: { context: 'fetching active message' },
      });
    }
    return null;
  }
};
